import { opportunitiesStages, oppToStepper } from 'app/services/sfAuth/sfData/sfOpportunity'
import { getLabelFromTranslationData } from 'app/views/common/TranslationsCommon'

/**
 * Retrieves the appropriate form ID based on the opportunity stage, configurations, and other parameters.
 * @function
 * @param {Object} params - The parameters for determining the form.
 * @param {Object} params.config - The configuration object containing form data.
 * @param {string} params.stage - The current stage of the opportunity.
 * @param {string} [params.grantForm] - The form associated with the grant, if any.
 * @param {boolean} params.wasHardCoded - Whether the form was hardcoded.
 * @param {string} params.oldFormVersionField - The previous form version.
 * @param {boolean} [params.printView=false] - Whether to use the print view form.
 * @returns {string|null} The appropriate form ID or null if no form is applicable.
 */
export const getProperFormForOpportunity = ({
  config,
  stage,
  grantForm,
  wasHardCoded,
  oldFormVersionField,
  printView = false
}): string | null | undefined => {
  const shouldUseCurrentForm =
    opportunitiesStages.IN_PROGRESS === stage

  const savedForm = grantForm || oldFormVersionField
  let formId = shouldUseCurrentForm ? config?.form : savedForm

  if (wasHardCoded && !grantForm && !savedForm) {
    if (printView || opportunitiesStages.MORE_INFO_REQUIERED !== stage) {
      formId = config?.applicationViewForHardcoded
    } else {
      return null
    }
  }

  return formId
}

/**
 * Maps applications data to be used in a data table, including relevant details like funding stream, PM contact, and actions.
 * @function
 * @param {Object} params - The parameters for mapping applications.
 * @param {Array} params.applications - List of application objects.
 * @param {Array} params.fundingStreams - List of funding stream objects.
 * @param {Object} params.formsConfig - Configuration for the forms.
 * @param {string} params.language - The language for translations.
 * @param {Array} params.opportunitiesWithContract - List of opportunities with contracts.
 * @returns {Array} A mapped array of application objects ready for the data table.
 */
export const mapApplicationsForDataTable = ({
  applications,
  fundingStreams,
  formsConfig,
  language,
  opportunitiesWithContract
}) => {
  return applications.map((item, index) => {
    const mail = item.info.pmContact?.split(';')[0]
    const fundingStream =
      fundingStreams.find(
        (fs) => fs.id === item.info.fundingStream
      ) || undefined
    const formId = fundingStream?.form
    const printForm = fundingStream?.printForm
    const formConfig = formId ? formsConfig[formId] : null
    const recordType = item.info.fundTypeName

    const lastModifiedDate = item.info.lastModifiedByUserDate || item.info.lastModifiedDate

    return {
      name: item.info.name,
      recordType,
      fundingStream: fundingStream?.id,
      fund:
        fundingStream &&
        getLabelFromTranslationData({
          langVersion: language,
          data: fundingStream?.fullName
        }),
      stageName: item.info.stageName,
      createdDate: item.info.createdDate,
      pm: {
        isPm: Boolean(item.info.pmName && mail),
        assignedManager: item.info.assignedManager,
        pmName: item.info.pmName,
        pmMail: mail,
        pmPhone: item.info.pmContact?.split(';')[1]
      },
      actions: {
        formId,
        appToPrint: item,
        fundingStream: fundingStream?.id,
        fundingStreamApplicationForm: fundingStream?.form,
        hasReadAccess: item.info.hasReadAccess,
        editAccess: item.info.hasEditAccess,
        printForm,
        hasContract: opportunitiesWithContract.includes(
          item.info.id
        ),
        formConfig,
        wasHardCoded: item.info.hardCoded,
        oldFormVersion: item.info.oldFormVersion,
        grantForm: item.info.grantForm,
        dataIndex: index,
        recordType,
        id: item.info.id,
        pm: {
          isPm: Boolean(item.info.pmName && mail),
          assignedManager: item.info.assignedManager,
          pmName: item.info.pmName,
          pmMail: mail,
          pmPhone: item.info.pmContact?.split(';')[1]
        },
        isPrequalification: false,
        stageName: item.info.stageName
      },
      lastModifiedUserData: item.info.lastModifiedUserData,
      lastModifiedDate,
      id: item.info.id,
      dataTableItemType: 'opportunity',
      type: item.attributes.type
      // block print for new forms
    }
  })
}

/**
 * Maps prequalification data for use in a data table, with similar details to applications but specific to prequalification information.
 * @function
 * @param {Object} params - The parameters for mapping prequalification data.
 * @param {Array} params.prequalifications - List of prequalification objects.
 * @param {Array} params.fundingStreams - List of funding stream objects.
 * @param {string} params.language - The language for translations.
 * @returns {Array} A mapped array of prequalification objects ready for the data table.
 */
export const mapPrequalificationsForDataTable = ({
  prequalifications,
  fundingStreams,
  language
}) => {
  return prequalifications.map((item, index) => {
    const fundingStream =
      fundingStreams.find((fs) => fs.id === item.fundingStream) ||
      undefined

    return {
      name: item.name,
      stageName: item.stage,
      createdDate: item.createdDate,
      fundingStream: fundingStream?.id,
      fund: getLabelFromTranslationData({
        langVersion: language,
        data: fundingStream?.fullName
      }),
      pm: {
        isPm: Boolean(item.assignedProgramManager),
        assignedManager: item.assignedProgramManager,
        pmName: item.assignedProgramManagerDetails.name,
        pmMail: item.assignedProgramManagerDetails.mail,
        pmPhone: item.assignedProgramManagerDetails.phone
      },
      actions: {
        editAccess: true,
        dataIndex: index,
        fundingStream: fundingStream?.id,
        fundingStreamApplicationForm: fundingStream?.form,
        id: item.id,
        stageName: item.stage,
        isPrequalification: true,
        pm: {
          isPm: Boolean(item.assignedProgramManager),
          assignedManager: item.assignedProgramManager,
          pmName: item.assignedProgramManagerDetails.name,
          pmMail: item.assignedProgramManagerDetails.mail,
          pmPhone: item.assignedProgramManagerDetails.phone
        }
      },
      lastModifiedDate: item.lastModifiedDate,
      id: item.id,
      isPrequalification: true,
      dataTableItemType: 'prequalification',
      type: item?.attributes?.type || undefined
      // block print for new forms
    }
  })
}

interface StepperItem {
  info: {
    recordTypeId: string
    fundTypeName: string
    name: string
    stageName: string
  }
}

/**
 * Maps opportunities data to a stepper format, excluding some conditions like read access and project type, and processes upcoming funds.
 * @function
 * @param {Array} opportunities - List of opportunity objects to be mapped.
 * @param {Object} upcomingFunds - An object storing upcoming funds data.
 * @returns {Array} A mapped array of opportunities in stepper format.
 */
export const mapOpportunitiesToStepper = (
  opportunities,
  upcomingFunds
) => {
  return opportunities
    .filter(
      (opp) =>
        opp.UserRecordAccess.HasReadAccess &&
        // Filter out Development Project opportunities
        opp.RecordTypeId !== '012Am0000004LchIAE'
    )
    .map((item) => {
      const stepperItem = oppToStepper(item) as StepperItem
      const fundId = stepperItem.info.recordTypeId
      const fundName = stepperItem.info.fundTypeName
      const oppName = stepperItem.info.name
      const stageName = stepperItem.info.stageName

      const objectType = item.attributes.type

      if (upcomingFunds.hasOwnProperty(fundId) && stageName === 'In Progress') {
        upcomingFunds[fundId].fundName = fundName
        upcomingFunds[fundId].opportunities.push(oppName)
      }

      return stepperItem
    })
}

/**
 * Extracts the forms configuration, mapping them to specific configurations, and identifies the organization form.
 * @function
 * @param {Array} forms - List of form objects.
 * @param {Object} configurations - Configuration object with form-related data.
 * @returns {Object} An object containing the forms configuration, forms map, and the organization form.
 */
export const extractFormsConfig = (forms, configurations) => {
  const formsConfig = {}
  const formsMap = {}
  let organizationForm = null

  forms.forEach((form) => {
    const objects = form.config.objectsConnected || []
    formsConfig[form.id] = [...objects]
    formsMap[form.id] = form

    if (form.id === configurations.FORM_ORGANIZATION_DETAILS) {
      organizationForm = form
    }
  })

  return { formsConfig, formsMap, organizationForm }
}

/**
 * Retrieves user role permissions (Manager, Contributor, Partner, Associate) based on the organization member role.
 * @function
 * @param {Object} user - The user object to retrieve role permissions for.
 * @returns {Object} An object containing the role permissions for the user.
 */
export const getApplicationRolePermissions = (user) => {
  let userRole = 'No role'
  if (user.organizationMember) {
    userRole = user.organizationMember.TeamMemberRole
  }

  return {
    manager: userRole === 'Manager',
    contributor: userRole === 'Contributor',
    partner: userRole === 'Operator',
    associate: userRole === 'Associate'
  }
}
